import React, { useContext, useEffect, useRef, useState } from 'react';
import dragDropImage from './drag-drop.png';
import { ServiceAPI } from '../../../Api/ServiceAPI';
import { hasLivePreviewPermission, ServiceInfo, UserContext } from '../../../Utilis';
import { ReactComponent as ConfirmIcon } from "../../../Png/check-solid.svg";
import { useNavigate } from 'react-router-dom';

const DragAndDrop = ({ appName }) => {
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploaded, setUploaded] = useState(false);
  const [showConfUploBtn, setShowConfUploBtn] = useState(true);
  const [progressByFile, setFileProgressByFile] = useState({});
  const [fileTypeError, setFileTypeError] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [isConnectingPCloud, setIsConnectingPCloud] = useState(false);
  const { user } = useContext(UserContext);
  const controller = useRef(null);
  const oauthPollRef = useRef(null);
  const oauthCloseWatcherRef = useRef(null);
  const [cameraVideoFile, setCameraVideoFile] = useState(null);
  const [showMailParagraph, setShowMailParagraph] = useState(false);
  const [serviceName, setServiceName] = useState('');
  const navigate = useNavigate();

  const stripFileName = (name) => name.replaceAll("#", "").trim();

  const acceptedTypes = [
    'video/mp4',
    'video/avi',
    'video/quicktime',
    'video/webm',
    'application/zip',
    'application/x-zip-compressed',
    'application/x-rar-compressed',
  ];

  const acceptedExtensions = ['mp4', 'avi', 'mov', 'webm', 'zip', 'rar'];

  const getFileExtension = (fileName) => fileName.split('.').pop().toLowerCase();

  const normalizeFolderId = (value) => {
    const normalized = String(value ?? '').trim();
    const lowerValue = normalized.toLowerCase();

    if (!normalized || lowerValue === 'null' || lowerValue === 'undefined' || lowerValue === 'nan' || lowerValue === 'false') {
      return null;
    }

    return /^[1-9]\d*$/.test(normalized) ? normalized : null;
  };

  const resetUploadUiAfterError = () => {
    setUploaded(true);
    setUploadProgress(0);
    setShowConfUploBtn(true);
  };

  const openPCloudConnect = (authUrl) => {
    if (!authUrl) {
      setFileTypeError('pCloud OAuth URL is missing.');
      return;
    }

    if (oauthPollRef.current) {
      clearInterval(oauthPollRef.current);
      oauthPollRef.current = null;
    }

    if (oauthCloseWatcherRef.current) {
      clearInterval(oauthCloseWatcherRef.current);
      oauthCloseWatcherRef.current = null;
    }

    setIsConnectingPCloud(true);
    setFileTypeError(
      'pCloud nije spojen. Otvorena je autorizacija u novom tabu. Nakon odobrenja pristupa vrati se ovdje.'
    );

    const oauthWindow = window.open(authUrl, '_blank');

    let pollCount = 0;
    const maxPolls = 60;

    const handleMessage = (event) => {
      if (event?.data?.type === 'PCLOUD_OAUTH_SUCCESS') {
        stopPolling();
        setIsConnectingPCloud(false);
        setFileTypeError('pCloud je uspješno spojen. Klikni ponovno na upload.');
        return;
      }

      if (event?.data?.type === 'PCLOUD_OAUTH_ERROR') {
        stopPolling();
        setIsConnectingPCloud(false);
        setFileTypeError(event?.data?.error || 'OAuth nije uspio.');
      }
    };

    const stopPolling = () => {
      if (oauthPollRef.current) {
        clearInterval(oauthPollRef.current);
        oauthPollRef.current = null;
      }

      if (oauthCloseWatcherRef.current) {
        clearInterval(oauthCloseWatcherRef.current);
        oauthCloseWatcherRef.current = null;
      }

      window.removeEventListener('message', handleMessage);
    };

    const pollStatus = async () => {
      try {
        const statusRes = await ServiceAPI.getPCloudStatus();

        if (statusRes?.data?.connected) {
          stopPolling();
          setIsConnectingPCloud(false);
          setFileTypeError('pCloud je uspješno spojen. Klikni ponovno na upload.');
        } else {
          pollCount += 1;
        }
      } catch (err) {
        pollCount += 1;
      }

      if (pollCount >= maxPolls) {
        stopPolling();
        setIsConnectingPCloud(false);
        setFileTypeError(
          'pCloud povezivanje nije potvrđeno. Ako si odobrio pristup, pokušaj ponovno upload.'
        );
      }
    };

    window.addEventListener('message', handleMessage);

    oauthPollRef.current = setInterval(pollStatus, 3000);

    oauthCloseWatcherRef.current = setInterval(() => {
      if (oauthWindow && oauthWindow.closed) {
        clearInterval(oauthCloseWatcherRef.current);
        oauthCloseWatcherRef.current = null;

        setTimeout(() => {
          pollStatus();
        }, 500);
      }
    }, 1000);
  };

  const getPCloudAccessToken = async () => {
    try {
      const resLogin = await ServiceAPI.login();

      if (resLogin?.data?.result === 0 && resLogin?.data?.auth) {
        return resLogin.data.auth;
      }

      if (resLogin?.data?.connected === false && resLogin?.data?.authUrl) {
        openPCloudConnect(resLogin.data.authUrl);
        return null;
      }

      throw new Error(resLogin?.data?.message || 'Failed to connect to pCloud.');
    } catch (error) {
      const status = error?.response?.status;
      const data = error?.response?.data;

      if ((status === 409 || status === 404) && data?.authUrl) {
        openPCloudConnect(data.authUrl);
        return null;
      }

      throw error;
    }
  };

  const getParentServiceFolderId = async (serviceNumber, fallbackFolderId) => {
    const existingFolderId = normalizeFolderId(fallbackFolderId);
    if (existingFolderId) {
      return existingFolderId;
    }

    try {
      const response = await ServiceAPI.ensureServiceFolder(serviceNumber);
      const repairedFolderId = normalizeFolderId(response?.data?.folderId);
      if (repairedFolderId) {
        return repairedFolderId;
      }

      throw new Error(response?.data?.message || 'Could not prepare service folder.');
    } catch (error) {
      const status = error?.response?.status;
      const data = error?.response?.data;

      if ((status === 409 || status === 404) && data?.authUrl) {
        openPCloudConnect(data.authUrl);
        return null;
      }

      throw error;
    }
  };

  const handleUpload = async () => {
    let _accessToken = '';

    setUploadProgress(0);
    setUploaded(false);
    setFileTypeError('');

    if (controller.current && !controller.current.signal.aborted) {
      controller.current.abort();
    }
    controller.current = new AbortController();

    try {
      const { sqlNameId: service, serviceNumber } = ServiceInfo[appName];

      if (uploadedFiles.length === 0) {
        setFileTypeError('No files selected.');
        return;
      }

      if (!service || serviceNumber < 0) {
        setFileTypeError('Service configuration is invalid.');
        return;
      }

      if (serviceName.trim() === '') {
        setFileTypeError('Folder name is empty.');
        return;
      }

      if (!user) {
        setFileTypeError('User is not authenticated.');
        return;
      }

      _accessToken = await getPCloudAccessToken();

      if (!_accessToken) {
        setShowConfUploBtn(true);
        setUploadProgress(0);
        return;
      }

      setIsConnectingPCloud(false);

      const parentFolderId = await getParentServiceFolderId(serviceNumber, user[service]);

      if (!parentFolderId) {
        setShowConfUploBtn(true);
        setUploadProgress(0);
        return;
      }

      const folderResponse = await ServiceAPI.createFolder(
        _accessToken,
        parentFolderId,
        stripFileName(serviceName)
      );

      if (!folderResponse?.data || folderResponse.data.result !== 0) {
        throw new Error(folderResponse?.data?.error || 'Failed to create folder.');
      }

      const folderNumber = folderResponse.data.metadata.folderid;

      let data = {
        folderName: serviceName.trim(),
        folderId: folderNumber,
        serviceNumber,
        karmenId: user.Id,
      };

      const serviceResponse = await ServiceAPI.createService(data);

      if (!serviceResponse?.data) {
        throw new Error('Failed to create service.');
      }

      data = { ...data, ...serviceResponse.data };
      setShowConfUploBtn(false);

      const uploadResponses = await Promise.allSettled(
        uploadedFiles.map((file, index) =>
          ServiceAPI.upload_file(
            _accessToken,
            serviceResponse.data.folderId,
            file,
            setFileProgressByFile,
            index,
            controller.current.signal
          )
        )
      );

      uploadedFiles.forEach((file, index) => {
        const result = uploadResponses[index];

        if (result.status === 'fulfilled') {
          if (acceptedTypes.includes(file.type) || acceptedExtensions.includes(getFileExtension(file.name))) {
            const uploadData = result.value?.data;
            if (uploadData?.metadata?.[0]?.fileid) {
              data.videoId = uploadData.metadata[0].fileid;
              data.videoName = file.name;
            }
          }

          setFileProgressByFile((prev) => ({
            ...prev,
            [index]: 100,
          }));
        } else {
          console.error(`File ${file.name} failed to upload.`, result.reason);
          setFileProgressByFile((prev) => ({
            ...prev,
            [index]: 'Error',
          }));
        }
      });

      await ServiceAPI.createVideoUpload(data);

      setUploaded(true);
      setUploadProgress(0);
      setShowConfUploBtn(true);
      setUploadedFiles([]);
      setFileTypeError('');
      setFileProgressByFile({});
      setServiceName('');
    } catch (error) {
      console.error(error);

      const errorMessage =
        error?.response?.data?.details?.error ||
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        'Something went wrong. Try again.';

      setFileTypeError(errorMessage);
      resetUploadUiAfterError();
      setUploadedFiles([]);
      setFileProgressByFile({});
      setServiceName('');
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setDragOver(false);
    setFileTypeError('');

    const files = event.dataTransfer.files;
    const newFiles = [];

    if (user && files && files.length > 0) {
      for (let i = 0; i < files.length; i++) {
        const fileExtension = getFileExtension(files[i].name);

        if (
          (acceptedTypes.includes(files[i].type) || acceptedExtensions.includes(fileExtension)) &&
          !uploadedFiles.find((element) => element.name === files[i].name)
        ) {
          newFiles.push(files[i]);
        } else {
          setFileTypeError(`${files[i].name} is not a supported file type.`);
        }
      }

      setUploadedFiles((prevFiles) => [...prevFiles, ...newFiles]);
    }
  };

  const handleFileSelect = (event) => {
    event.preventDefault();
    setFileTypeError('');

    const files = event.target.files;
    const newFiles = [];

    if (user && files && files.length > 0) {
      for (let i = 0; i < files.length; i++) {
        const fileExtension = getFileExtension(files[i].name);

        if (
          (acceptedTypes.includes(files[i].type) || acceptedExtensions.includes(fileExtension)) &&
          !uploadedFiles.find((element) => element.name === files[i].name)
        ) {
          newFiles.push(files[i]);
        } else {
          setFileTypeError(`${files[i].name} is not a supported file type.`);
        }
      }

      setUploadedFiles((prevFiles) => [...prevFiles, ...newFiles]);
    }

    event.target.value = '';
  };

  const handleButtonClick = () => {
    document.getElementById('fileInput').click();
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    setDragOver(true);
    event.dataTransfer.dropEffect = 'copy';
  };

  const handleDragLeave = () => {
    setDragOver(false);
  };

  const handleClearUploadedFiles = () => {
    setUploadedFiles([]);
    setFileTypeError('');
    setUploaded(false);
    setShowConfUploBtn(true);
    setUploadProgress(0);
    setFileProgressByFile({});
    setIsConnectingPCloud(false);

    if (controller.current && !controller.current.signal.aborted) {
      controller.current.abort();
    }

    if (oauthPollRef.current) {
      clearInterval(oauthPollRef.current);
      oauthPollRef.current = null;
    }

    if (oauthCloseWatcherRef.current) {
      clearInterval(oauthCloseWatcherRef.current);
      oauthCloseWatcherRef.current = null;
    }
  };

  const download_instr = (filename) => {
    setShowMailParagraph(true);
    const link = document.createElement('a');
    link.href = require('./../../../Png/' + filename);
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDownloadInstructions = () => {
    try {
      switch (appName) {
        case ServiceInfo.Biofouling.Name:
          download_instr(ServiceInfo.Biofouling.instructionsPath);
          break;
        case ServiceInfo.TableApp.Name:
          download_instr(ServiceInfo.TableApp.instructionsPath);
          break;
        case ServiceInfo.Custom.Name:
          download_instr(ServiceInfo.Custom.instructionsPath);
          break;
        case ServiceInfo.SalmorApp.Name:
          download_instr(ServiceInfo.SalmorApp.instructionsPath);
          break;
        case ServiceInfo.BiomassApp.Name:
          download_instr(ServiceInfo.BiomassApp.instructionsPath);
          break;
        case ServiceInfo.Mortality.Name:
          download_instr(ServiceInfo.Mortality.instructionsPath);
          break;
        case ServiceInfo.Waste.Name:
          download_instr(ServiceInfo.Waste.instructionsPath);
          break;
        case ServiceInfo.Inspection.Name:
          download_instr(ServiceInfo.Inspection.instructionsPath);
          break;
        case ServiceInfo.Cagedetection.Name:
          download_instr(ServiceInfo.Cagedetection.instructionsPath);
          break;
        case ServiceInfo.FishCounting.Name:
          download_instr(ServiceInfo.FishCounting.instructionsPath);
          break;
        default:
          download_instr(ServiceInfo.TableApp.instructionsPath);
          break;
      }
    } catch (error) {
      console.error('Error generating instruction download:', error);
    }
  };

  const handleFileRemove = (index) => {
    const newFiles = uploadedFiles.filter((_, i) => i !== index);
    setUploadedFiles(newFiles);
  };

  useEffect(() => {
    let count = 0;
    let totalPercent = 0;

    Object.values(progressByFile).forEach((value) => {
      if (typeof value === 'number' && value >= 0) {
        totalPercent += value;
        count += 1;
      }
    });

    const progress = count > 0 ? totalPercent / count : 0;
    setUploadProgress(progress);
  }, [progressByFile]);

  useEffect(() => {
    if (cameraVideoFile) {
      setUploadedFiles((prevFiles) => [...prevFiles, cameraVideoFile]);
    }
  }, [cameraVideoFile]);

  useEffect(() => {
    return () => {
      if (oauthPollRef.current) {
        clearInterval(oauthPollRef.current);
      }

      if (oauthCloseWatcherRef.current) {
        clearInterval(oauthCloseWatcherRef.current);
      }

      if (controller.current && !controller.current.signal.aborted) {
        controller.current.abort();
      }
    };
  }, []);

  return (
    <div className={`container ${dragOver ? 'drag-over' : ''}`}>
      <h1>{ServiceInfo[appName].displayName}</h1>

      {hasLivePreviewPermission(user, appName) ? (
        <h2
          style={{ cursor: "pointer", marginBottom: "10px" }}
          onClick={() => {
            navigate("livepreview");
          }}
        >
          Live preview
        </h2>
      ) : null}

      <button className="download-button" onClick={handleDownloadInstructions}>
        Download Instructions
      </button>

      {showMailParagraph && appName === ServiceInfo.Biofouling.Name ? (
        <b>
          <p>
            Send us an e-mail (contact@karmenstudio.ai) with data requested on instruction document above
          </p>
        </b>
      ) : null}

      <div
        className="drag-drop-container"
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
      >
        <div className="drag-drop-area">
          <img src={dragDropImage} alt="Drag and drop" className="drag-drop-image" />
          <p>
            <b>Drag and drop files here</b>
          </p>
          <p className="drag-drop-text">
            <b>or</b>
          </p>
          <div>
            <button className="fileselect-btn" onClick={handleButtonClick}>
              Select files
            </button>
            <input
              type="file"
              id="fileInput"
              style={{ display: 'none' }}
              accept="video/mp4,video/avi,video/quicktime,video/webm,application/zip,application/x-zip-compressed,application/x-rar-compressed"
              multiple
              onChange={handleFileSelect}
            />
          </div>
        </div>
      </div>

      {uploadedFiles.length > 0 && (
        <>
          <div
            style={{ margin: '5px', backgroundColor: '#DAC625', padding: '10px', borderRadius: '10px' }}
            className="uploaded-files"
          >
            <p style={{ fontSize: '20px', color: '#000' }}>Selected files</p>

            <div>
              <label
                style={{
                  fontWeight: '700',
                  marginRight: '5px',
                  cursor: 'default',
                  color: 'black'
                }}
              >
                Folder name:
              </label>
              <input
                style={{
                  width: '40%',
                  padding: '10px',
                  borderRadius: '5px',
                  border: '1px solid #ccc',
                  marginBottom: '20px',
                }}
                type="text"
                value={serviceName}
                onChange={(event) => setServiceName(event.target.value)}
              />
            </div>

            {uploadedFiles.map((file, index) => (
              <div
                key={`${file.name}-${file.lastModified}`}
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                }}
              >
                <p style={{ fontWeight: '700', marginRight: '5px', cursor: 'default', color: 'black' }}>
                  Name:
                </p>
                <p style={{ fontSize: '16px', marginRight: '5px', cursor: 'default', color: 'black' }}>
                  {file.name}
                </p>

                {progressByFile.hasOwnProperty(index) ? (
                  progressByFile[index] === 'Error' ? (
                    <span style={{ color: 'red' }}>Upload failed</span>
                  ) : (
                    <span style={{ fontWeight: '700' }}>
                      <img
                        style={{ paddingLeft: '5px' }}
                        width={'15px'}
                        src={require('../../../Png/upload.webp')}
                        alt="Upload Icon"
                      />{' '}
                      {progressByFile[index] === -1
                        ? 'Already exists'
                        : `${progressByFile[index]} %`}
                    </span>
                  )
                ) : (
                  <img
                    style={{ cursor: 'pointer' }}
                    src={require('../../../Png/delete.webp')}
                    height={'16px'}
                    alt="Delete Icon"
                    onClick={() => handleFileRemove(index)}
                  />
                )}
              </div>
            ))}
          </div>

          {showConfUploBtn ? (
            <button className="confirm" onClick={handleUpload}>
              {isConnectingPCloud ? 'Reconnect pCloud' : 'Upload selected files'}
            </button>
          ) : null}
        </>
      )}

      <div className="upload-progress-container">
        {(uploadProgress > 0 || !showConfUploBtn) && (
          <>
            <progress value={uploadProgress} max="100"></progress>
            <span>{uploadProgress.toFixed(2)}%</span>
          </>
        )}

        {uploaded && (
          <>
            <p className="upload-done-info">
              Upload completed
              <ConfirmIcon className="icon" />
            </p>
            {ServiceInfo[appName].after_download_info &&
              ServiceInfo[appName].after_download_info.map((s, index) => (
                <p key={index} style={{ color: '#354a4b' }}>
                  {s}
                </p>
              ))}
          </>
        )}
      </div>

      {fileTypeError && <p className="error">{fileTypeError}</p>}

      {uploadedFiles.length > 0 && !showConfUploBtn && (
        <button
          style={{ backgroundColor: "#d41e1e", marginTop: "10px" }}
          className="cancel"
          onClick={handleClearUploadedFiles}
        >
          Cancel Upload
        </button>
      )}
    </div>
  );
};

export default DragAndDrop;
